import { useCallback, useEffect, useRef } from 'react';

import { useEventCallback } from '#src/utils/use-event-callback.js';

import type { ViewportState } from '#src/utils/events.js';

/**
 * Minimum per-dimension difference for two viewports to be considered meaningfully distinct.
 * Filters floating-point noise while still detecting any intentional pan or zoom.
 */
const VIEWPORT_DIFF_EPSILON = 1e-6;

/**
 * Returns true when two viewports differ by more than the noise threshold in any dimension.
 * @param a - First viewport state.
 * @param b - Second viewport state.
 * @returns Whether the viewports are meaningfully different.
 */
export function viewportsDiffer(a: ViewportState, b: ViewportState): boolean {
	return (
		Math.abs(a.centerX - b.centerX) > VIEWPORT_DIFF_EPSILON ||
		Math.abs(a.centerY - b.centerY) > VIEWPORT_DIFF_EPSILON ||
		Math.abs(a.scale - b.scale) > VIEWPORT_DIFF_EPSILON
	);
}

export interface UseSelectionHistoryControllerOptions {
	/**
	 * Set the delayed viewport on the current history entry.
	 * Called once the camera has been stationary for `delayedThresholdMs`.
	 */
	updateCurrentDelayedViewport: (viewport: ViewportState) => void;
	/**
	 * Set the resting viewport on the current history entry.
	 * Called once the camera has been stationary for `restingThresholdMs`.
	 */
	updateCurrentRestingViewport: (viewport: ViewportState) => void;
	/**
	 * Request a pan-checkpoint history entry be pushed with the given settled viewport.
	 * Called once the camera has been stationary for `panCheckpointThresholdMs` and the
	 * settled viewport meaningfully differs from the last pushed entry's viewport.
	 */
	onPanCheckpointNeeded: (viewport: ViewportState) => void;
	/** Returns true when the current history entry is the latest (no forward history). */
	isAtLatestEntry: () => boolean;
	/**
	 * Returns the viewport of the most recently pushed history entry (the initial, un-mutated
	 * value captured at push time). Used to decide whether a pan-checkpoint is warranted.
	 */
	getLastPushedViewport: () => ViewportState | null;
	/** Milliseconds of idle time before `updateCurrentDelayedViewport` is called (default: 2000). */
	delayedThresholdMs?: number;
	/** Milliseconds of idle time before `updateCurrentRestingViewport` is called (default: 4000). */
	restingThresholdMs?: number;
	/** Milliseconds of idle time before `onPanCheckpointNeeded` is called (default: 8000). */
	panCheckpointThresholdMs?: number;
}

/**
 * Manages viewport-related side effects for selection history.
 *
 * The current entry's `viewport` field is captured once at push time and never mutated —
 * it is the guaranteed fallback for restoration. This hook then enriches an entry
 * progressively as the user dwells:
 *
 * - After `delayedThresholdMs` (default 2 s) of no movement: calls `updateCurrentDelayedViewport`.
 * - After `restingThresholdMs` (default 4 s) of no movement: calls `updateCurrentRestingViewport`.
 * - After `panCheckpointThresholdMs` (default 8 s): calls `onPanCheckpointNeeded` when the
 *   settled viewport differs from the last pushed entry.
 *
 * All timers are cancelled when navigation begins or a new entry is pushed.
 * Updates are suppressed when not at the latest history entry.
 * All pending timers are cancelled on unmount.
 *
 * @returns Stable imperative handles to drive the controller from event handlers and callbacks.
 */
export function useSelectionHistoryController({
	updateCurrentDelayedViewport,
	updateCurrentRestingViewport,
	onPanCheckpointNeeded,
	isAtLatestEntry,
	getLastPushedViewport,
	delayedThresholdMs = 2000,
	restingThresholdMs = 4000,
	panCheckpointThresholdMs = 8000,
}: UseSelectionHistoryControllerOptions) {
	const stableUpdateDelayedViewport = useEventCallback(updateCurrentDelayedViewport);
	const stableUpdateRestingViewport = useEventCallback(updateCurrentRestingViewport);
	const stableOnPanCheckpointNeeded = useEventCallback(onPanCheckpointNeeded);
	const stableIsAtLatestEntry = useEventCallback(isAtLatestEntry);
	const stableGetLastPushedViewport = useEventCallback(getLastPushedViewport);

	// Thresholds are static config; capture once at mount.
	const thresholdsRef = useRef({ delayedThresholdMs, restingThresholdMs, panCheckpointThresholdMs });

	const isNavigatingRef = useRef(false);
	const delayedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const restingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const panCheckpointTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearIdleTimers = useCallback(() => {
		if (delayedTimerRef.current !== null) {
			clearTimeout(delayedTimerRef.current);
			delayedTimerRef.current = null;
		}
		if (restingTimerRef.current !== null) {
			clearTimeout(restingTimerRef.current);
			restingTimerRef.current = null;
		}
		if (panCheckpointTimerRef.current !== null) {
			clearTimeout(panCheckpointTimerRef.current);
			panCheckpointTimerRef.current = null;
		}
	}, []);

	useEffect(() => clearIdleTimers, [clearIdleTimers]);

	/**
	 * Called on every viewport-changed event. Resets all idle timers.
	 * No-ops when navigating or when the current entry is not the latest.
	 * Does NOT immediately mutate any entry field — the initial `viewport` captured at push
	 * time is preserved as-is.
	 * @param viewport - The new viewport state from the canvas.
	 */
	const onViewportChanged = useCallback(
		(viewport: ViewportState) => {
			if (isNavigatingRef.current || !stableIsAtLatestEntry()) {
				return;
			}
			clearIdleTimers();

			delayedTimerRef.current = setTimeout(() => {
				delayedTimerRef.current = null;
				if (!isNavigatingRef.current && stableIsAtLatestEntry()) {
					stableUpdateDelayedViewport(viewport);
				}
			}, thresholdsRef.current.delayedThresholdMs);

			restingTimerRef.current = setTimeout(() => {
				restingTimerRef.current = null;
				if (!isNavigatingRef.current && stableIsAtLatestEntry()) {
					stableUpdateRestingViewport(viewport);
				}
			}, thresholdsRef.current.restingThresholdMs);

			panCheckpointTimerRef.current = setTimeout(() => {
				panCheckpointTimerRef.current = null;
				if (!isNavigatingRef.current && stableIsAtLatestEntry()) {
					const lastPushed = stableGetLastPushedViewport();
					if (lastPushed === null || viewportsDiffer(viewport, lastPushed)) {
						stableOnPanCheckpointNeeded(viewport);
					}
				}
			}, thresholdsRef.current.panCheckpointThresholdMs);
		},
		[
			clearIdleTimers,
			stableIsAtLatestEntry,
			stableUpdateDelayedViewport,
			stableUpdateRestingViewport,
			stableGetLastPushedViewport,
			stableOnPanCheckpointNeeded,
		],
	);

	/**
	 * Called when history navigation begins (Back/Forward/jump).
	 * Cancels pending timers and suppresses all viewport updates until `onNavigationEnded`.
	 */
	const onNavigationStarted = useCallback(() => {
		isNavigatingRef.current = true;
		clearIdleTimers();
	}, [clearIdleTimers]);

	/**
	 * Called when history navigation ends.
	 * Re-enables viewport tracking for subsequent user-driven camera movement.
	 */
	const onNavigationEnded = useCallback(() => {
		isNavigatingRef.current = false;
	}, []);

	/**
	 * Called whenever a new real history entry is pushed (selection change, design change, or
	 * pan-checkpoint). Cancels pending timers so they do not fire against the new entry
	 * using stale viewport data from the previous context.
	 */
	const onNewEntryPushed = useCallback(() => {
		clearIdleTimers();
	}, [clearIdleTimers]);

	return { onViewportChanged, onNavigationStarted, onNavigationEnded, onNewEntryPushed, cancelTimers: clearIdleTimers };
}
